// One shape for every admin form.
//
// The forms had drifted: some used labels, some relied on placeholders alone,
// and the two that mattered most — department and designation — were free text,
// so an officer could be filed under "roads" or "Roads and Transport" or a typo,
// none of which match the department a complaint is routed to.
//
// A placeholder is not a label. It disappears the moment someone types, so a
// half-completed form stops explaining itself, and screen readers treat it as a
// hint rather than a name. Every field here is labelled, and anything with a
// known set of valid answers is a select rather than a text box.
import React from 'react';
import { IconChevronDown, IconAlertTriangle } from '../dashboard/icons';

const CONTROL =
  'w-full bg-surface rounded-lg border border-line px-3 py-2.5 text-[13.5px] text-ink '
  + 'outline-none transition-all placeholder:text-ink-faint '
  + 'focus:border-leaf-500 focus:ring-2 focus:ring-leaf-500/15 '
  + 'disabled:bg-surface-inset disabled:text-ink-muted disabled:cursor-not-allowed';

const LABEL = 'block text-[11px] font-semibold uppercase tracking-wide text-ink-muted mb-1.5';

export function Field({ id, label, hint, required = false, children }) {
  return (
    <div>
      <label className={LABEL} htmlFor={id}>
        {label}
        {required && <span className="text-danger-600 ml-1" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11.5px] text-ink-faint mt-1.5 leading-snug">{hint}</p>}
    </div>
  );
}

export function TextField({ id, label, hint, required, ...props }) {
  return (
    <Field id={id} label={label} hint={hint} required={required}>
      <input id={id} className={CONTROL} required={required} {...props} />
    </Field>
  );
}

/**
 * A select with a real empty option.
 *
 * `placeholder` is the text shown while nothing is chosen. It stays selectable
 * unless the field is required, so a value can be cleared again — a dropdown
 * you cannot un-choose is a trap when someone picks the wrong row.
 */
export function SelectField({
  id, label, hint, required, options = [], placeholder = 'Choose…', value, onChange, ...props
}) {
  return (
    <Field id={id} label={label} hint={hint} required={required}>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={onChange}
          required={required}
          className={`${CONTROL} appearance-none pr-9 cursor-pointer`}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => {
            const val = typeof o === 'string' ? o : o.value;
            const text = typeof o === 'string' ? o : o.label;
            return <option key={val} value={val}>{text}</option>;
          })}
          {/* A stored value that is no longer in the list still has to be
              selectable, or opening the form would silently change it. */}
          {value && !options.some((o) => (typeof o === 'string' ? o : o.value) === value) && (
            <option value={value}>{value}</option>
          )}
        </select>
        <IconChevronDown
          size={16}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none"
        />
      </div>
    </Field>
  );
}

/**
 * The password for a brand new account: generated by the server, or chosen here.
 *
 * Generated is the default, and deliberately so. A password an administrator
 * invents while filling in a form is chosen under time pressure, gets reused
 * across the accounts they create that afternoon, and has to be passed on
 * verbally or over chat -- which means it exists somewhere other than the
 * account holder's head. Omit the field and the server picks one and emails it
 * to the person it belongs to, and nobody else ever sees it.
 *
 * Choosing one by hand stays available, because "no mail server is configured"
 * is currently true and somebody has to be able to work around it.
 */
export function NewAccountPassword({
  idPrefix, generate, onGenerateChange, value, onChange,
}) {
  return (
    <div className="rounded-lg border border-line bg-surface-inset/50 p-3">
      <label htmlFor={`${idPrefix}-generate`} className="flex items-start gap-2.5 cursor-pointer">
        <input
          id={`${idPrefix}-generate`}
          type="checkbox"
          checked={generate}
          onChange={(e) => onGenerateChange(e.target.checked)}
          className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer rounded border-line accent-leaf-600"
        />
        <span>
          <span className="block text-[13px] font-semibold text-ink">
            Generate a password and email it to them
          </span>
          <span className="mt-0.5 block text-[11.5px] leading-snug text-ink-faint">
            The server picks one and sends it straight to the new account holder, so it
            never passes through anybody else — including you.
          </span>
        </span>
      </label>

      {!generate && (
        <div className="mt-3.5 pt-3.5 border-t border-line">
          <TextField
            id={`${idPrefix}-password`}
            label="Temporary password"
            type="password"
            required
            value={value}
            onChange={onChange}
            autoComplete="new-password"
            hint={`At least ${MIN_NEW_PASSWORD} characters. You are choosing it, so you also have to
                   pass it on — note it down before you submit, because this screen will not show
                   it again.`}
          />
        </div>
      )}
    </div>
  );
}

// Mirrors the floor in the backend schemas (FieldWorkerCreate /
// SystemOfficialCreate both set min_length=8). Checked here so a short password
// is a sentence under the field rather than a 422 round trip.
export const MIN_NEW_PASSWORD = 8;

export function FormError({ children }) {
  if (!children) return null;
  return (
    <p role="alert" className="flex items-start gap-2 text-[13px] font-medium text-danger-700 bg-danger-50 border border-danger-100 rounded-lg px-3 py-2">
      <IconAlertTriangle size={14} className="shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  );
}

export function SubmitRow({ busy, label, busyLabel, onCancel }) {
  return (
    <div className="flex gap-2 pt-1">
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="focus-ring flex-1 bg-surface border border-line hover:bg-surface-inset text-ink-body font-semibold text-[13.5px] py-2.5 rounded-lg transition-colors"
        >
          Cancel
        </button>
      )}
      <button
        type="submit"
        disabled={busy}
        className="focus-ring flex-1 inline-flex items-center justify-center gap-2 bg-leaf-600 hover:enabled:bg-leaf-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-[13.5px] py-2.5 rounded-lg shadow-sm transition-all"
      >
        {busy ? busyLabel || 'Saving…' : label}
      </button>
    </div>
  );
}

// The designations the department actually uses. A free-text box here produced
// "Sr. Officer", "Senior officer" and "SO" for the same job.
export const DESIGNATIONS = [
  'Grievance Officer',
  'Senior Grievance Officer',
  'Ward Officer',
  'Assistant Engineer',
  'Executive Engineer',
  'Department Head',
];
